
// These are constants that have to do with the various Data Types

import { DisablableOption } from "@models";
import { 
  CREATURE_TYPES,
  CHAR_FEATURE_TYPES, 
  ITEM_TYPES, 
  SCHOOLS,
  RARITIES,
  ABILITY_SCORES,
  SOURCE_BOOK_TYPES,
  EFFECT_TYPES,
  ATTACK_TYPES,
  FEATURE_SOURCE_TYPE_OPTIONS,
  SIZE_OPTIONS,
  CR_OPTIONS,
  DAMAGE_TYPES,
  ABILITY_SCORES_EXPANDED,
  FEAT_TIERS,
  SPELL_CASTING_TIME_OPTIONS,
  CASTING_TIME_OPTIONS,
  LANGUAGE_TYPES,
  WORLD_SETTINGS,
  BASTION_FACILITY_SIZES,
  SPELL_LEVEL_OPTIONS,
  TICKET_TYPES,
} from "@dndModels/Constants";
import { CampaignObjectType, DataType } from "./Types";
import { MORAL_ALIGNMENTS, ORDER_ALIGNMENTS } from "./interfaces";

const standard = ["source_book","effect_type","damage_type","attack_type","target_type","is_dominant","world_settings"];
const standard_field_map = { "source_book": "source_book_ids", "effect_type": "the_effect_types", "attack_type": "the_attack_types", "damage_type": "damage_types" };
const fs_standard = [...standard,"feature_type","tags"];
const fs_standard_field_map = { ...standard_field_map, "feature_type": "feature_types" };

export const TRUST_JSON_FOR_DATA_TYPES: string[] = [
  // Main Things
  "source_book",
  "standard_features",
  // "floating_feature",
  // "creature_feature",
  "reusable_string",
  "optional_feature_base",
  // Character Origins
  "race",
  "subrace",
  "background",
  // Class Related
  "game_class",
  "subclass",
  "special_feature",
  "pact_boon",
  "eldritch_invocation",
  "feat",
  "fighting_style",
  "table",
  // Items
  "item",
  "magic_item_keyword",
  "tool",
  "weapon_mastery_property",
  "game_card",
  "game_deck",
  "equipment_pack",
  // Other Feature Sources
  "condition",
  "gift",
  "bastion_facility",
  // Spells
  "spell",
  "spell_list",
  "spell_slot_type",
  // Transforms
  "transform",
  "adaptation",
  // Peripherals
  "resource",
  "org_position",
  "organization",
  "rollable_table",
  "template",
  "deity",
  // Creatures
  // "creature",
  "creature_group",
  "creature_gift",
  // Other
  "ticket"
];
// export const TRUST_JSON_FOR_DATA_TYPES: string[] = [
//   // Main Things
//   // "source_book",
//   "standard_features",
//   "floating_feature",
//   "creature_feature",
//   "reusable_string",
//   "optional_feature_base",
//   // Character Origins
//   "race",
//   "subrace",
//   "background",
//   // Class Related
//   "game_class",
//   "subclass",
//   "special_feature",
//   "pact_boon",
//   "eldritch_invocation",
//   "feat",
//   "fighting_style",
//   "table",
//   // Items
//   "item",
//   "magic_item_keyword",
//   "tool",
//   "weapon_mastery_property",
//   "game_card",
//   "game_deck",
//   "equipment_pack",
//   // Other Feature Sources
//   "condition",
//   "gift",
//   "bastion_facility",
//   // Spells
//   "spell",
//   "spell_list",
//   "spell_slot_type",
//   // Transforms
//   "transform",
//   "adaptation",
//   // Peripherals
//   "resource",
//   "org_position",
//   "organization",
//   "rollable_table",
//   "template",
//   "deity",
//   // Creatures
//   "creature",
//   "creature_group",
//   "creature_gift",
//   // Other
//   "ticket"
// ];
export const PLAYER_OBJECT_TYPES = [
  "character",
  "campaign",
  "encounter_log",
  "org_instance",
  "campaign_deck",
  "character_receivable",
  "character_drawn_card",
  "user_ticket",
];
export const CAMPAIGN_OBJECT_TYPES: CampaignObjectType[] = [
  "campaign_object",
  "campaign_recipe",
  "campaign_players",
  "campaign_player",
  "campaign_char",
  "campaign_char_options",
  "campaign_deck", 
  "campaign_note", 
  "campaign_npc",
  "encounter",
]
export const OTHER_OBJECT_TYPES: string[] = [
  "deity",
  "equipment_pack",
  "loot_table",
  "organization",
  "resource",
  "rollable_table",
  "spell_list",
  "spell_slot_type",
  "table",
  "template_base",
  "tool",
  "spell",
  "game_card",
  "game_deck",
  "creature_group",
  "ticket",
  "error_log",
  "user"
];
export const OTHER_NON_CACHED_OBJECTS: DataType[] = [
  "creature",
  "sb_section"
];
export const SUGGESTION_FIELD_DATA_TYPE_MAP: { [field: string]: DataType[] } = {
  native_planes: ["creature","creature_group","deity"],
  habitats: ["creature_group"],
  age_category: ["creature"],
  subtypes: ["creature"],
  divine_domains: ["deity"],
  pantheons: ["deity"],
  gift_type: ["gift"]
};
export const FIELDS_FOR_DATA_TYPE: { 
  [data_type: string]: {
    fields: string[],
    field_map: { [field: string]: string }, // for if it needs to change the field name when returning
  }
} = {
  "adaptation": { fields: ["source_book","transform_type"], field_map: { "source_book": "source_book_ids" } },
  "race": { fields: [...fs_standard,"creature_size","creature_type"], field_map: {...fs_standard_field_map,"creature_size": "sizes"} },
  "background": { fields: [...fs_standard,"feat","ability_scores"], field_map: {...fs_standard_field_map,"feat": "feat_ids"}},
  "class_group": { fields: standard, field_map: standard_field_map },
  "condition": { fields: [...fs_standard,"common","immunity_exists","encumbrance","exhaustion"], field_map: fs_standard_field_map },
  "creature": { 
    fields: ["source_book","creature_type","subtypes","swarm","creature_group","age_category","min_cr","max_cr","flying","swimming","legendary","creature_size","environments","world_settings","native_planes","effect_type","attack_type","is_dominant"], 
    field_map: {...standard_field_map,"creature_group": "group_id", "creature_size": "size_num", "min_cr": "challenge_rating", "max_cr": "challenge_rating" } },
  "creature_group": { fields: standard, field_map: standard_field_map },
  "deity": { fields: [...standard,"pantheons","moral","order","divine_domains"], field_map: standard_field_map },
  "eldritch_invocation": { fields: [...fs_standard,"pact_boon"], field_map: {...fs_standard_field_map, "pact_boon": "pact_boon_ids" } },
  "equipment_pack": { fields: ["source_book"], field_map: standard_field_map },
  "feat": { fields: [...fs_standard, "ability_scores", "asi_min","feat_tier"], field_map: {...fs_standard_field_map} },
  "floating_feature": { fields: ["source_book","feature_type","effect_type","damage_type","attack_type","utility_detail","resource"], field_map: {...standard_field_map, "utility_detail": "the_utility_details", "resource": "resource_costs" } },
  "creature_feature": { fields: ["source_book","feature_type","effect_type","damage_type","attack_type","utility_detail","resource"], field_map: {...standard_field_map, "utility_detail": "the_utility_details", "resource": "resource_costs" } },
  "game_class": { fields: [...standard, "sidekick","tags"], field_map: standard_field_map },
  "gift": { fields: [...fs_standard,"gift_type"], field_map: fs_standard_field_map },
  "subrace": { fields: [...fs_standard,"race"], field_map: {...fs_standard_field_map, "race": "race_ids" } },
  "item": { fields: [...fs_standard,"item_type","rarities","magical","weapon_keyword","attunement"], field_map: {...fs_standard_field_map, "weapon_keyword": "weapon_keyword_ids"} },
  "magic_item_keyword": { fields: [...fs_standard,"item_type","rarities","attunement"], field_map: {...fs_standard_field_map, "item_type": "base_item_types"} },
  // "org_position": { fields: fs_standard, field_map: fs_standard_field_map },
  // "organization": { fields: ["source_book"], field_map: standard_field_map },
  "pact_boon": { fields: fs_standard, field_map: fs_standard_field_map },
  "Skill": { fields: ["ability_score"], field_map: {"ability_score": "use_ability_score" } },
  "tool": { fields: ["source_book"], field_map: {...standard_field_map, } },
  "resource": { fields: ["source_book"], field_map: standard_field_map },
  "rollable_table": { fields: ["source_book"], field_map: standard_field_map },
  "source_book": { fields: ["source_book_type", "public", "third_party", "ua"], field_map: standard_field_map },
  "special_feature": { fields: [...fs_standard,"special_feature_type"], field_map: {...fs_standard_field_map, "special_feature_type": "type" } },
  "spell": { 
    fields: [ ...standard,"school","spell_level","spell_casting_time","spell_list","ritual","concentration","components","material_consumed","disease","charm","sight","contaminated","upcasts"], 
    field_map: { "source_book": "source_book_ids", "spell_casting_time": "casting_time", "effect_type": "effect_types","damage_type": "damage_types", "attack_type": "attack_types", "target_type": "target_types", "magic_school": "school_id", "magic_element": "element_ids", "spell_level": "level", "spell_list": "spell_list_ids" } },
  "spell_list": { fields: ["source_book"], field_map: {} },
  "standard_features": { fields: [...fs_standard], field_map: {...fs_standard_field_map} },
  "subclass": { fields: [...fs_standard,"game_class"], field_map: {...fs_standard_field_map, "game_class": "game_class_id" } },
  "table": { fields: ["source_book"], field_map: {...standard_field_map, } },
  "transform": { fields: ["source_book","transform_type"], field_map: {...standard_field_map, } },
  "weapon_keyword": { fields: ["source_book"], field_map: {} },
  "weapon_mastery_property": { fields: ["source_book","item_type","effect_type","damage_type","attack_type"], field_map: {...standard_field_map, } },
  "character": { fields: ["race","subrace","game_class","subclass","campaign","tags"], field_map: {
    "race": "race_ids", "subrace": "subrace_ids", "game_class": "class_ids", "subclass": "subclass_ids", "campaign": "campaign_id","tags": "fs_tags"
  }},
  "game_card": { fields: ["source_book","game_deck"], field_map: { "source_book": "source_book_ids", "game_deck": "deck_ids" } },
  "game_deck": { fields: ["source_book"], field_map: { "source_book": "source_book_ids" } },
  "bastion_facility": { 
    fields: ["source_book","bastion_size","special_facility"], 
    field_map: { "source_book": "source_book_ids", "bastion_size": "size" }
  },
  "ticket": { fields: ["ticket_type"], field_map: { } },
  "error_log": { fields: [], field_map: { } },
};
export const FIELD_DISPLAY_MAP: { [field: string]: string } = {
  source_book: "Source Book",
  source_book_type: "Source Book Type",
  public: "Public",
  third_party: "Third Party",
  ua: "Unearthed Arcana",
  game_class: "Class",
  subclass: "Subclass",
  race: "Race",
  subrace: "Subrace",
  weapon_keyword: "Weapon Keyword",
  creature_type: "Creature Type",
  swarm: "Swarm",
  creature_group: "Creature Group",
  feature_type: "Feature Type",
  item_type: "Item Type",
  gift_type: "Gift Type",
  language_type: "Language Type",
  tool_type: "Tool Type",
  effect_type: "Effect Type",
  damage_type: "Damage Type",
  attack_type: "Attack Type",
  target_type: "Target Type",
  utility_detail: "Utility Detail",
  school: "School",
  rarities: "Rarity",
  spell_list: "Spell List",
  special_feature_type: "Special Feature Type",
  environments: "Environment",
  world_settings: "Setting",
  native_planes: "Native Plane",
  ability_score: "Ability Score",
  spell_level: "Spell Level",
  spell_casting_time: "Casting Time",
  casting_time: "Casting Time",
  user: "User",
  campaign: "Campaign",
  // special_feat_type: "Feat Type",
  pact_boon: "Pact Boon",
  ritual: "Ritual",
  concentration: "Concentration",
  transform_type: "Transform Type",
  transform: "Transform",
  adaptation: "Adaptation",
  components: "Components",
  material_consumed: "Consumes Material Component",
  feature_source_type: "Feature Source Type",
  min_cr: "Minimum CR",
  max_cr: "Maximum CR",
  flying: "Flying",
  swimming: "Swimming",
  legendary: "Legendary",
  creature_size: "Creature Size",
  subtypes: "Creature Subtype",
  age_category: "Age Catogory",
  creature: "Creature",
  resource: "Resource",
  // prof_type: "Proficiency Type",
  // magic_school: "Magic School",
  // magic_element: "Magic Element",
  proficient: "Proficient",
  requirements: "Requirements Met",
  game_deck: "Deck",
  magical: "Magical",
  disease: "Disease",
  charm: "Charm",
  sight: "Sight",
  contaminated: "Contaminated",
  upcasts: "Increases Effects on Upcast",
  ability_scores: "Ability Scores",
  asi_min: "Ability Score Increase Minimum",
  sidekick: "Sidekick",
  feat_tier: "Feat Tier",
  tags: "Tags",
  is_dominant: "Dominant Variant",
  common: "Common",
  immunity_exists: "Immunity Exists",
  encumbrance: "Encumbrance",
  exhaustion: "Exhaustion",
  on_list: "Selected",
  attunement: "Attunement",
  feat: "Feat",
  known: "Known",
  prepared: "Prepared",
  bastion_size: "Bastion Size",
  special_facility: "Special Facility",
  selected: "Selected",
  ticket_type: "Ticket Type",
  pantheons: "Pantheons",
  moral: "Moral Alignment",
  order: "Order Alignment",
  divine_domains: "Divine Domains"
}
export const USE_ID = [
  "source_book", 
  "game_class",
  "subclass",
  "race",
  "subrace",
  "weapon_keyword",
  "spell_list",
  "user",
  "campaign",
  "pact_boon",
  "transform",
  "adaptation",
  "creature",
  "creature_group",
  "resource",
  "game_deck",
  // "magic_school",
  // "magic_element",
  "feat"
];
export const CONSTANTIZED_DATA_TYPES = ["action_type","armor_type","language","sense","skill","weapon_keyword"];
export const USE_FIELD_DATA_TYPE: { [field: string]: DataType | "" } = { // : { field: data_type }
  special_feature_type: "special_feature",
  gift_type: "gift",
  tool_type: "tool",
  habitats: "creature_group",
  native_planes: "creature_group",
  // feat_type: "feat",
  transform_type: "transform",
  subtypes: "creature",
  age_category: "creature",
  tags: "",
  pantheons: "deity",
  divine_domains: "deity"
}
export const USE_CONSTANT_LIST: { [field: string]: string[] | DisablableOption[] } = {
  creature_type: CREATURE_TYPES,
  feature_type: CHAR_FEATURE_TYPES,
  item_type: ITEM_TYPES,
  school: SCHOOLS,
  rarities: RARITIES,
  ability_score: ABILITY_SCORES,
  spell_casting_time: SPELL_CASTING_TIME_OPTIONS,
  casting_time: CASTING_TIME_OPTIONS,
  source_book_type: SOURCE_BOOK_TYPES,
  effect_type: EFFECT_TYPES,
  damage_type: DAMAGE_TYPES,
  attack_type: ATTACK_TYPES,
  // prof_type: PROFICIENCY_TYPES,
  target_type: ["X Targets","Area","Self","None"],
  components: ["V","S","M"],
  feature_source_type: FEATURE_SOURCE_TYPE_OPTIONS,
  creature_size: SIZE_OPTIONS,
  ability_scores: ABILITY_SCORES_EXPANDED,
  feat_tier: FEAT_TIERS,
  // special_feat_type: ["None","Fighting Style"],
  language_type: LANGUAGE_TYPES,
  world_settings: WORLD_SETTINGS,
  bastion_size: BASTION_FACILITY_SIZES,
  ticket_type: TICKET_TYPES,
  moral: MORAL_ALIGNMENTS,
  order: ORDER_ALIGNMENTS
}
export const DONT_SORT_LIST: string[] = ["spell_level","rarities","ability_score","ability_scores","ability","casting_time","spell_casting_time","feat_tier","source_book_type","min_cr","max_cr"];
export const IS_SINGLE: string[] = ["rarities"];
export const NO_EXCLUDE: string[] = ["rarities"];
export const USE_BOOLEAN: string[] = [
  "ritual",
  "concentration",
  "material_consumed",
  "flying","swimming",
  "legendary",
  "proficient",
  "requirements",
  "magical",
  "disease",
  "charm",
  "sight",
  "contaminated",
  "upcasts",
  "sidekick",
  "is_dominant",
  "common",
  "immunity_exists",
  "encumbrance",
  "exhaustion",
  "on_list",
  "attunement",
  "known",
  "prepared",
  "special_facility",
  "selected",
  "public",
  "third_party",
  "ua",
  "swarm"
];
export const USE_INT: string[] = ["min_cr","max_cr","asi_min","spell_level"];
export const USE_STRING: string[] = ["utility_detail"];
export const INT_CONSTANTS: { [field: string]: string[] | DisablableOption[] } = {
  min_cr: CR_OPTIONS,
  max_cr: CR_OPTIONS,
  asi_min: ["0","1","2"],
  spell_level: SPELL_LEVEL_OPTIONS,
}
export const INT_DEFAULTS: { [field: string]: number } = {
  min_cr: 0,
  max_cr: 50
}